import React, {useEffect, useRef, useState, useMemo} from 'react'
import {defineMessage, defineMessages, useIntl, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Heading,
    List,
    ListItem,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useAddressBookFields from './useAddressBookFields'
import Field from '../../components/field'
import {useAddressSuggestion} from '../../integrations/melissa/hooks/use-melissa'
import debounce from 'lodash/debounce'
import {useOutsideClick} from '@chakra-ui/react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {expandAddressSuggestions} from '../../utils/address-utils'
import {PlusIcon} from '../../components/icons'
import {ChevronDownType2} from '../icons'
const formTitleAriaLabel = defineMessage({
    defaultMessage: 'Address Form',
    id: 'use_address_fields.label.address_form'
})

const ADDRESS_SUGGESTION_LIMIT = 4
const UNITS_PER_GROUP = 60
const sectionLabels = defineMessages({
    labelHeading: {
        id: 'address_book_fields.label_heading',
        defaultMessage: 'Label'
    },
    addressHeading: {
        id: 'address_book_fields.address_heading',
        defaultMessage: 'Address'
    },
    aptOfficeHeading: {
        id: 'address_book_fields.apt_office_heading',
        defaultMessage: 'Add app, office, etc.'
    }
})

const AddressBookFields = ({form, prefix = ''}) => {
    const fields = useAddressBookFields({form, prefix})
    const intl = useIntl()
    const theme = useTheme()
    const {baseStyle, addressModal} = theme.components.AddressBook
    const addressFormRef = useRef()
    const boxRef = useRef()
    const [address1, setAddress1] = useState('')
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [showApt, setShowApt] = useState(false)
    const [expandedGroups, setExpandedGroups] = useState({})

    // Auto-show apartment field when editing an address that already has one
    const apartmentValue = form.watch(fields.apartment?.name)
    useEffect(() => {
        if (apartmentValue && !showApt) {
            setShowApt(true)
        }
    }, [apartmentValue])

    useOutsideClick({
        ref: boxRef,
        handler: () => setShowSuggestions(false)
    })

    const countryCode = form.watch('countryCode')
    const {isLoading, suggestions, fetchSuggestions} = useAddressSuggestion(address1, countryCode)

    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const ADDRESS_SUGGESTIONS_ENABLED =
        getSiteEnvVarValue(config.app, 'ADDRESS_SUGGESTIONS_ENABLED', siteId) === 'true'
    const ADDRESS_SERVICES_DEBOUNCE = getSiteEnvVarValue(
        config.app,
        'ADDRESS_SERVICES_DEBOUNCE_INTERVAL',
        siteId
    )
    const ADDRESS_SERVICES_DEBOUNCE_INTERVAL = ADDRESS_SERVICES_DEBOUNCE
        ? Number(ADDRESS_SERVICES_DEBOUNCE)
        : 300

    // Group suggestions by base address for unit-level display
    const groupedSuggestions = useMemo(() => {
        if (!suggestions || suggestions.length === 0) return []
        
        // Normalize for grouping/dedupe so accents/case variations of the same
        // value (e.g. "Montréal" vs "Montreal") are treated as equivalent.
        const normalize = (value) =>
            (value || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .trim()
                .toLowerCase()

        // Melissa may collapse all units for a base address into a single
        // suggestion whose address2 is a comma-separated list (e.g.
        // "Ste R17,Ste R36,..."). Expand those into one suggestion per unit
        // so the grouping logic below can present them individually.
        const expandedSuggestions = expandAddressSuggestions(suggestions)

        // Standalone addresses (no unit) are always shown as individual items.
        // Only unit-level suggestions (with address2) are grouped together.
        const standalones = expandedSuggestions.filter((s) => !s.address2)
        const withUnits = expandedSuggestions.filter((s) => !!s.address2)

        // Group unit-level suggestions by normalized base address
        const groups = {}
        withUnits.forEach((s) => {
            const key = `${normalize(s.address1)}|${normalize(s.city)}|${normalize(s.postalCode)}`
            if (!groups[key]) {
                groups[key] = {
                    key,
                    address1: s.address1,
                    city: s.city,
                    postalCode: s.postalCode,
                    items: []
                }
            }
            // Dedupe units inside the same group by normalized address2
            const unitKey = normalize(s.address2)
            if (!groups[key].items.some((existing) => normalize(existing.address2) === unitKey)) {
                groups[key].items.push(s)
            }
        })

        // Dedupe standalones against each other and against any group header
        // (a standalone that matches a group's base address is already
        // represented by the group, so drop it).
        const seenStandaloneKeys = new Set()
        const dedupedStandalones = []
        standalones.forEach((s) => {
            const key = `${normalize(s.address1)}|${normalize(s.city)}|${normalize(s.postalCode)}`
            if (seenStandaloneKeys.has(key)) return
            if (groups[key]) return
            seenStandaloneKeys.add(key)
            dedupedStandalones.push(s)
        })

        // Build display items: standalones first, then grouped unit entries
        const result = dedupedStandalones.map((s) => ({type: 'single', suggestion: s}))

        Object.values(groups).forEach((group) => {
            // Sort items by address2 ascending (natural numeric order)
            const sortedItems = [...group.items].sort((a, b) =>
                (a.address2 || '').localeCompare(b.address2 || '', undefined, {numeric: true, sensitivity: 'base'})
            )

            if (sortedItems.length === 1) {
                result.push({type: 'single', suggestion: sortedItems[0]})
            } else {
                // Split into sub-groups of UNITS_PER_GROUP
                for (let i = 0; i < sortedItems.length; i += UNITS_PER_GROUP) {
                    const chunk = sortedItems.slice(i, i + UNITS_PER_GROUP)
                    const start = i + 1
                    const end = i + chunk.length
                    const groupKey = `${group.address1}|${start}-${end}`
                    result.push({
                        type: 'group',
                        key: groupKey,
                        label: [group.address1, group.city, group.postalCode].filter(Boolean).join(', '),
                        rangeLabel: `${start}-${end}`,
                        items: chunk
                    })
                }
            }
        })

        return result
    }, [suggestions])

    // Auto-expand groups with manageable number of apartments
    useEffect(() => {
        if (!groupedSuggestions || groupedSuggestions.length === 0) {
            setExpandedGroups({})
            return
        }

        const nextExpandedGroups = {}
        groupedSuggestions.forEach((suggestion) => {
            // Auto-expand groups with 15 or fewer apartments
            if (suggestion.type === 'group' && suggestion.items.length <= 15) {
                nextExpandedGroups[suggestion.key] = true
            }
        })
        setExpandedGroups(nextExpandedGroups)
    }, [groupedSuggestions])

    const debouncedFetchSuggestionsRef = useRef(
        debounce((data) => {
            if (ADDRESS_SUGGESTIONS_ENABLED) {
                const {value, shouldFetch} = data
                if (value.length >= 7) {
                    fetchSuggestions({address: value, shouldFetch})
                }
            }
        }, ADDRESS_SERVICES_DEBOUNCE_INTERVAL)
    )

    useEffect(() => {
        if (address1.length >= 7) {
            debouncedFetchSuggestionsRef.current({value: address1, shouldFetch: true})
        }
    }, [address1])

    const customLabelProps = {
        labelProps: {
            color: 'grey.600 !important',
            fontSize: 'md',
            lineHeight: '24px'
        }
    }

    const handleAddress1Change = (e) => {
        const value = e.target.value
        setAddress1(value)
        form.setValue(fields.address1.name, value)
        form.trigger(fields.address1.name)
        setShowSuggestions(true)
    }

    const handleSuggestionClick = (suggestion) => {
        if (suggestion.address2) {
            form.setValue(fields.apartment.name, suggestion.address2)
            setShowApt(true)
        }
        setAddress1(suggestion.address1)
        form.setValue(fields.address1.name, suggestion.address1)
        form.setValue(fields.city.name, suggestion.city)
        form.setValue(fields.stateCode.name, suggestion.stateCode)
        form.setValue(fields.postalCode.name, suggestion.postalCode)
        setShowSuggestions(false)
    }

    return (
        <Stack
            spacing={4}
            aria-label={intl.formatMessage(formTitleAriaLabel)}
            tabIndex="0"
            ref={addressFormRef}
        >
            <Heading {...addressModal.fieldHeading} {...baseStyle.centerContent} as="h4">
                {intl.formatMessage(sectionLabels.labelHeading)}
            </Heading>
            <Field {...fields.addressTitle} {...customLabelProps} />

            <Heading {...addressModal.fieldHeading} {...baseStyle.centerContent} as="h4">
                {intl.formatMessage(sectionLabels.addressHeading)}
            </Heading>
            <Field {...fields.firstName} {...customLabelProps} />
            <Field {...fields.lastName} {...customLabelProps} />
            <Box ref={boxRef} position="relative">
                <Field
                    {...fields.address1}
                    {...customLabelProps}
                    inputProps={{
                        ...fields.address1.inputProps,
                        onChange: handleAddress1Change,
                        onKeyUp: () => setShowSuggestions(true),
                        autoComplete: 'off',
                        maxLength: 50
                    }}
                />
                {showSuggestions && suggestions.length >= 1 && (
                    <List
                        position="absolute"
                        bg="white"
                        w="full"
                        zIndex={3}
                        shadow="dark-lg"
                        borderColor="gray.700"
                        maxH="200px"
                        overflow="auto"
                    >
                        {isLoading ? (
                            <ListItem py={3} display="flex" justifyContent="center">
                                <Spinner size="sm" />
                            </ListItem>
                        ) : (
                            groupedSuggestions.slice(0, ADDRESS_SUGGESTION_LIMIT).map((suggestion, index) => {
                                if (suggestion.type === 'single') {
                                    suggestion = suggestion.suggestion
                                    const formattedSuggestion = [
                                        suggestion.address1,
                                        suggestion.address2,
                                        suggestion.city,
                                        suggestion.countryCode,
                                        suggestion.postalCode
                                    ]
                                    .filter(Boolean)
                                    .join(', ')

                                return (
                                    <ListItem
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        py={3}
                                        px={2}
                                        fontSize="sm"
                                        borderColor="cardDivider"
                                        borderBottomWidth={
                                            index < suggestions.length - 1 ? '1px' : '0'
                                        }
                                        _hover={{bg: 'gray.100', cursor: 'pointer'}}
                                    >
                                        {formattedSuggestion}
                                    </ListItem>
                                    )
                                } 
                                else if (suggestion.type === 'group') {
                                    // Grouped (multi-unit) suggestion
                                    const isExpanded = expandedGroups[suggestion.key]
                                    return (
                                        <Box key={suggestion.key}>
                                            <ListItem
                                                {...addressModal.suggestionItem}
                                                {...addressModal.suggestionGroupHeader}
                                                onMouseEnter={() => setHighlightedIndex(-1)}
                                                onClick={() =>
                                                    setExpandedGroups((prev) => ({
                                                        ...prev,
                                                        [suggestion.key]: !prev[suggestion.key]
                                                    }))
                                                }
                                            >
                                                <Box flex="1">
                                                    <Text {...addressModal.suggestionGroupLabel}>
                                                        <FormattedMessage
                                                            defaultMessage="Show results for {range}"
                                                            id="address.suggestions.show_results"
                                                            values={{range: suggestion.rangeLabel}}
                                                        />
                                                    </Text>
                                                    <Text {...addressModal.suggestionGroupAddress} isTruncated>
                                                        {suggestion.label}
                                                    </Text>
                                                </Box>
                                                <ChevronDownType2
                                                    sx={{
                                                        width: '14px',
                                                        height: '14px',
                                                        transform: isExpanded
                                                            ? 'rotate(180deg)'
                                                            : 'rotate(0deg)',
                                                        transition: 'transform 0.2s ease-in-out'
                                                    }}
                                                />
                                            </ListItem>
                                            {isExpanded &&
                                                suggestion.items.map((unit, unitIdx) => (
                                                    <ListItem
                                                        key={unitIdx}
                                                        role="option"
                                                        onClick={() => handleSuggestionClick(unit)}
                                                        {...addressModal.suggestionItem}
                                                    >
                                                        {unit.address2 || unit.address1}
                                                    </ListItem>
                                                ))}
                                        </Box>
                                    )
                                }
                            })
                        )}
                    </List>
                )}
            </Box>

            <Heading
                {...addressModal.optionalFieldHeading}
                {...baseStyle.centerContent}
                as="h4"
                onClick={() => setShowApt(!showApt)}
            >
                <PlusIcon width={17} height={17} />
                {intl.formatMessage(sectionLabels.aptOfficeHeading)}
            </Heading>
            {showApt && <Field {...fields.apartment} {...customLabelProps} />}

            <Field {...fields.city} {...customLabelProps} />
            <Field
                {...fields.countryCode}
                {...customLabelProps}
                formControlProps={{isDisabled: true}}
                hideRequiredIndicator
            />
            <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
                <Field {...fields.stateCode} {...customLabelProps} displayValueAsLabel />
                <Field
                    {...fields.postalCode}
                    {...customLabelProps}
                    inputProps={{...fields.postalCode.inputProps, maxLength: 10}}
                />
            </SimpleGrid>
            <Field {...fields.phone} {...customLabelProps} />
            <Field {...fields.preferred} />
        </Stack>
    )
}

AddressBookFields.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string
}

export default AddressBookFields
